import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Learn from "@/pages/Learn";
import TopicDetail from "@/pages/TopicDetail";
import { ExamsList, ExamRunner, ExamResult } from "@/pages/Exams";
import Jobs from "@/pages/Jobs";
import Tools, { RiskMatrix } from "@/pages/Tools";
import CVBuilder from "@/pages/CVBuilder";
import Interview from "@/pages/Interview";
import InterviewSimulator from "@/pages/InterviewSimulator";
import Documents from "@/pages/Documents";
import Subscription from "@/pages/Subscription";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/learn" element={<Protected><Learn /></Protected>} />
      <Route path="/learn/topic/:slug" element={<Protected><TopicDetail /></Protected>} />
      <Route path="/exams" element={<Protected><ExamsList /></Protected>} />
      <Route path="/exams/run/:examId" element={<Protected><ExamRunner /></Protected>} />
      <Route path="/exams/result/:attemptId" element={<Protected><ExamResult /></Protected>} />
      <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
      <Route path="/tools" element={<Protected><Tools /></Protected>} />
      <Route path="/tools/cv" element={<Protected><CVBuilder /></Protected>} />
      <Route path="/tools/interview" element={<Protected><Interview /></Protected>} />
      <Route path="/tools/simulator" element={<Protected><InterviewSimulator /></Protected>} />
      <Route path="/tools/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/tools/risk" element={<Protected><RiskMatrix /></Protected>} />
      <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
